import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('streamingServiceProvider');
export interface StreamingServiceProviderInfoRec {
  id: string;
  userId: string;
  serviceName?: string;
  subscriptionPlan?: string;
  monthlyCost?: number;
  billingCycleDate?: string;
  autopayEnabled?: boolean;
  paymentMethod?: string;
  accountEmail?: string;
  familySharing?: boolean;
  maxProfiles?: number;
  website?: string;
  notes?: string;
  username?: string;
  password?: string;
  isLocked?: boolean;
}

class StreamingServiceProviderInfoService {
  async getAllStreamingServiceProviderInfo(): Promise<StreamingServiceProviderInfoRec[]> {
    const response = await http.get('/api/streaming-service-provider-info');
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch streaming service provider information');
    }

    return response.data.data || [];
  }

  async getStreamingServiceProviderInfoById(id: string): Promise<StreamingServiceProviderInfoRec | null> {
    try {
      const response = await http.get(`/api/streaming-service-provider-info/${id}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch streaming service provider information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching streaming service provider information by ID:', error);
      return null;
    }
  }

  async createStreamingServiceProviderInfo(streamingServiceProviderData: Partial<StreamingServiceProviderInfoRec>): Promise<StreamingServiceProviderInfoRec> {
    const response = await http.post('/api/streaming-service-provider-info', streamingServiceProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save streaming service provider information');
    }

    return response.data.data;
  }

  async updateStreamingServiceProviderInfo(id: string, streamingServiceProviderData: Partial<StreamingServiceProviderInfoRec>): Promise<StreamingServiceProviderInfoRec> {
    const response = await http.patch(`/api/streaming-service-provider-info/${id}`, streamingServiceProviderData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update streaming service provider information');
    }

    return response.data.data;
  }

  async deleteStreamingServiceProviderInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/streaming-service-provider-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete streaming service provider information');
    }
  }

  async lockStreamingServiceProviderInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for streaming service provider ID:', id);
    const response = await http.post(`/api/streaming-service-provider-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock streaming service provider information');
    }
  }

  async unlockStreamingServiceProviderInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/streaming-service-provider-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock streaming service provider information');
    }
  }

  async getStreamingServiceProviderAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/streaming-service-provider-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }

    return response.data.data || [];
  }
}

export const streamingServiceProviderInfoService = new StreamingServiceProviderInfoService();
export default streamingServiceProviderInfoService;